import { useEffect, useId, useState } from 'react';
import styled from 'styled-components';
import { matchesPick } from '../services/teamSelections';
import { fetchGameOdds } from '../services/oddsService';

const Panel = styled.aside`
  grid-area: odds;
  background: ${props => props.theme.colors.tableBgColor};
  border: 1px solid ${props => props.theme.colors.borderColor};
  border-radius: 8px;
  padding: 16px;
  min-width: 0;
  h2 { font-size: 20px; margin: 0 0 8px; }
  p { font-size: 13px; margin: 8px 0; }
  a { color: ${props => props.theme.colors.buttonBgColor}; }
  button {
    background: ${props => props.theme.colors.buttonBgColor};
    color: ${props => props.theme.colors.buttonTextColor};
    border: 0;
    border-radius: 4px;
    padding: 6px 12px;
  }
`;
const DesktopHeading = styled.h2`
  @media (max-width: 850px) { display: none; }
`;
const MobileToggle = styled.button`
  && { display: none; }
  @media (max-width: 850px) {
    && {
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;
      text-align: left;
      font-size: 16px;
      font-weight: 600;
      cursor: pointer;
    }
  }
`;
const OddsContent = styled.div`
  @media (max-width: 850px) {
    display: ${props => props.$expanded ? 'block' : 'none'};
  }
`;
const Game = styled.article`
  border-top: 1px solid ${props => props.theme.colors.borderColor};
  padding: 12px 0;
  h3 { font-size: 14px; margin: 0 0 6px; }
  dl { display: flex; gap: 24px; margin: 8px 0; }
  dt { font-size: 12px; font-weight: normal; }
  dd { font-size: 14px; font-weight: 600; margin: 0; }
`;

const TeamLabel = styled.span`
  background: ${props => props.$selected ? '#1b4332' : 'transparent'};
  color: ${props => props.$selected ? '#95d5b2' : 'inherit'};
  border-radius: 4px;
  padding: ${props => props.$selected ? '2px 4px' : '0'};
`;

export default function GameOddsPanel({ year, week, selectedTeams = [], onGamesLoaded }) {
  const [expanded, setExpanded] = useState(false);
  const contentId = useId();
  const [state, setState] = useState({});
  const [retry, setRetry] = useState(0);
  const key = `${year}-${week}`;
  useEffect(() => {
    let active = true;
    setState({ key, loading: true });
    fetchGameOdds(year, week).then(
      data => { if (active) setState({ key, ...data }); },
      () => { if (active) setState({ key, error: true }); },
    );
    return () => { active = false; };
  }, [year, week, key, retry]);
  useEffect(() => {
    if (state.key === key && state.games) onGamesLoaded?.({ key, games: state.games });
  }, [state, key, onGamesLoaded]);
  const teamLabel = (team, moneyline = false) => {
    const selected = selectedTeams.some(pick => matchesPick(team, pick));
    return <TeamLabel $selected={selected}>
      {team.name}{moneyline && ` ${team.moneyline}`}{selected && ' ✓ Selected'}
    </TeamLabel>;
  };
  const loading = state.key !== key || state.loading;
  return (
    <Panel aria-label="Game odds">
      <DesktopHeading>Game odds</DesktopHeading>
      <MobileToggle type="button" aria-expanded={expanded} aria-controls={contentId} onClick={() => setExpanded(value => !value)}>
        <span>Game odds · Week {week}</span>
        <span aria-hidden="true">{expanded ? '−' : '+'}</span>
      </MobileToggle>
      <OddsContent id={contentId} $expanded={expanded}>
      <p>Week {week} · {year} · Regular season</p>
      <p>Reference only. Lines can change. Negative moneylines indicate the favorite; positive numbers indicate the underdog.</p>
      {loading ? <p role="status">Loading game odds…</p> : state.error ? (
        <div role="status">
          <p>Odds are unavailable right now. You can still make and save your picks.</p>
          <button onClick={() => setRetry(value => value + 1)}>Retry odds</button>
        </div>
      ) : <>
        <p>Fetched {new Date(state.fetchedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })} · Cached for 5 minutes</p>
        {!state.games?.length && <p>No games available for this week.</p>}
        {state.games?.map(game => (
          <Game key={game.id}>
            <h3>{teamLabel(game.away)} @ {teamLabel(game.home)}</h3>
            <p>{Number.isFinite(Date.parse(game.date)) ? new Date(game.date).toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' }) : 'Kickoff TBD'} · {game.status}</p>
            {game.hasOdds ? <>
              <dl>
                <div><dt>Spread</dt><dd>{game.spread}</dd></div>
                <div><dt>Total (O/U)</dt><dd>{game.total}</dd></div>
              </dl>
              <p>Moneyline: {teamLabel(game.away, true)} / {teamLabel(game.home, true)}</p>
              {game.provider && <p>Sportsbook: {game.provider}</p>}
            </> : <p>Odds not available for this game.</p>}
          </Game>
        ))}
      </>}
      <p>Source: <a href="https://www.espn.com/nfl/odds" target="_blank" rel="noreferrer">ESPN odds</a></p>
      </OddsContent>
    </Panel>
  );
}
